import { useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { renderAsync } from "docx-preview";
import {
  FileText,
  Download,
  Printer,
  X,
  Loader2,
  AlertCircle,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { normalizeDocumentUrl } from "@/lib/media-url";

const viewerSearchSchema = z.object({
  url: z.string().catch(""),
  name: z.string().optional().catch(""),
});

export const Route = createFileRoute("/document-viewer")({
  validateSearch: viewerSearchSchema,
  component: DocumentViewerPage,
});

type DocKind = "pdf" | "image" | "docx" | "text" | "unsupported";

function classifyDoc(fileName: string, url: string): DocKind {
  const target = fileName || url;
  const ext = target.split("?")[0].split("#")[0].split(".").pop()?.toLowerCase() || "";
  if (ext === "pdf") return "pdf";
  if (["png", "jpg", "jpeg", "webp", "gif", "svg", "bmp", "ico"].includes(ext)) return "image";
  if (["docx"].includes(ext)) return "docx";
  if (["txt", "json", "csv", "log", "md", "xml"].includes(ext)) return "text";
  return "unsupported";
}

function DocumentViewerPage() {
  const { url: rawUrl, name } = Route.useSearch();
  const url = normalizeDocumentUrl(rawUrl);
  const fileName = name || rawUrl.split("/").pop() || "Document";
  const kind = classifyDoc(fileName, rawUrl);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const docxContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = `${fileName} — HAMS Document Viewer`;
  }, [fileName]);

  useEffect(() => {
    return () => {
      if (pdfBlobUrl && pdfBlobUrl.startsWith("blob:")) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfBlobUrl]);

  useEffect(() => {
    if (!url) {
      setLoading(false);
      setError("No document URL provided");
      return;
    }

    let cancelled = false;

    if (kind === "image") {
      setLoading(false);
      return;
    }

    if (kind === "pdf") {
      setLoading(true);
      setError(null);
      fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
          return res.blob();
        })
        .then((blob) => {
          if (cancelled) return;
          const objectUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
          setPdfBlobUrl(objectUrl);
          setLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          // Fall back to direct URL in iframe
          setPdfBlobUrl(url);
          setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }

    if (kind === "text") {
      setLoading(true);
      setError(null);
      fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.text();
        })
        .then((txt) => {
          if (cancelled) return;
          setTextContent(txt);
          setLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          setError(`Failed to load text file: ${err.message}`);
          setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }

    if (kind === "docx") {
      setLoading(true);
      setError(null);
      fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.blob();
        })
        .then(async (blob) => {
          if (cancelled) return;
          if (docxContainerRef.current) {
            docxContainerRef.current.innerHTML = "";
            await renderAsync(blob, docxContainerRef.current, undefined, {
              className: "hams-docx-render",
              inWrapper: true,
              ignoreWidth: false,
              ignoreHeight: false,
              breakPages: true,
            });
          }
          setLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          setError(`Failed to render Word document: ${err.message}`);
          setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }

    setLoading(false);
  }, [url, kind]);

  const handleDownload = () => {
    const targetUrl = pdfBlobUrl || url;
    if (!targetUrl) return;
    const a = document.createElement("a");
    a.href = targetUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handlePrint = () => {
    window.print();
  };

  const extBadge = fileName.split(".").pop()?.toUpperCase() || kind.toUpperCase();

  return (
    <div className="flex h-screen w-screen flex-col bg-slate-900 text-slate-100 overflow-hidden font-sans">
      {/* Top Navigation Bar */}
      <header className="flex h-14 items-center justify-between border-b border-slate-800 bg-slate-950 px-4 flex-shrink-0 z-20">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/20 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-sm font-semibold text-slate-100 truncate max-w-md md:max-w-xl" title={fileName}>
                {fileName}
              </h1>
              <Badge variant="outline" className="text-[10px] uppercase font-mono px-1.5 py-0 border-slate-700 text-slate-300">
                {extBadge}
              </Badge>
            </div>
            <p className="text-[11px] text-slate-400">HAMS Document Viewer</p>
          </div>
        </div>

        {/* Toolbar Controls */}
        <div className="flex items-center gap-2">
          {kind === "image" && (
            <div className="flex items-center gap-1 border-r border-slate-800 pr-2 mr-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-slate-300 hover:text-white hover:bg-slate-800"
                onClick={() => setZoom((z) => Math.max(0.2, z - 0.2))}
                title="Zoom Out"
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-xs font-mono text-slate-400 w-12 text-center">
                {Math.round(zoom * 100)}%
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-slate-300 hover:text-white hover:bg-slate-800"
                onClick={() => setZoom((z) => Math.min(3, z + 0.2))}
                title="Zoom In"
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-slate-300 hover:text-white hover:bg-slate-800"
                onClick={() => setZoom(1)}
                title="Reset Zoom"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}

          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 text-xs text-slate-300 hover:text-white hover:bg-slate-800"
            onClick={handlePrint}
          >
            <Printer className="h-4 w-4" />
            <span className="hidden sm:inline">Print</span>
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 text-xs text-slate-300 hover:text-white hover:bg-slate-800"
            onClick={handleDownload}
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Download</span>
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-slate-400 hover:text-white hover:bg-slate-800 ml-1"
            onClick={() => window.close()}
            title="Close Tab"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </header>

      {/* Main Document Viewer Canvas */}
      <main className="relative flex-1 overflow-auto bg-slate-900/90 flex flex-col items-center">
        {loading && (
          <div className="flex h-full w-full flex-col items-center justify-center gap-3 text-slate-400">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm">Rendering document...</p>
          </div>
        )}

        {error && (
          <div className="flex h-full w-full flex-col items-center justify-center p-6 text-center">
            <div className="rounded-xl border border-red-900/50 bg-red-950/40 p-6 max-w-md">
              <AlertCircle className="mx-auto h-8 w-8 text-red-400 mb-2" />
              <h3 className="font-semibold text-red-200">Failed to render preview</h3>
              <p className="text-xs text-red-300/80 mt-1 mb-4">{error}</p>
              <Button size="sm" onClick={handleDownload} className="gap-1.5">
                <Download className="h-4 w-4" /> Download Original Document
              </Button>
            </div>
          </div>
        )}

        {/* PDF Viewer */}
        {!loading && !error && kind === "pdf" && (
          <iframe
            src={pdfBlobUrl || url}
            title={fileName}
            className="h-full w-full border-none bg-slate-900"
          />
        )}

        {/* Image Viewer */}
        {!loading && !error && kind === "image" && (
          <div className="flex min-h-full w-full items-center justify-center p-6 overflow-auto">
            <img
              src={url}
              alt={fileName}
              style={{ transform: `scale(${zoom})`, transformOrigin: "center center" }}
              className="max-h-[85vh] max-w-[90vw] object-contain rounded shadow-2xl transition-transform duration-100 ease-out border border-slate-800"
            />
          </div>
        )}

        {/* Word Document (DOCX) Viewer */}
        <div
          ref={docxContainerRef}
          className={`w-full max-w-4xl py-8 px-4 flex flex-col items-center ${
            kind === "docx" && !loading && !error ? "block" : "hidden"
          }`}
          style={{
            minHeight: "100%",
          }}
        />

        {/* Text Document Viewer */}
        {!loading && !error && kind === "text" && (
          <div className="w-full max-w-4xl p-6">
            <pre className="rounded-lg border border-slate-800 bg-slate-950 p-6 text-xs text-slate-200 font-mono whitespace-pre-wrap break-words leading-relaxed shadow-lg">
              {textContent}
            </pre>
          </div>
        )}

        {/* Unsupported Fallback */}
        {!loading && !error && kind === "unsupported" && (
          <div className="flex h-full w-full flex-col items-center justify-center p-6 text-center">
            <div className="rounded-xl border border-slate-800 bg-slate-950 p-8 max-w-md shadow-2xl">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-slate-800 text-primary mb-4">
                <FileText className="h-7 w-7" />
              </div>
              <h3 className="font-semibold text-slate-100 text-base">{fileName}</h3>
              <p className="text-xs text-slate-400 mt-1 mb-5">
                Inline preview is not supported for this file type ({extBadge}). You can download or open it with its native application.
              </p>
              <div className="flex justify-center gap-2">
                <Button size="sm" onClick={handleDownload} className="gap-1.5">
                  <Download className="h-4 w-4" /> Download File
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(url, "_blank")}
                  className="gap-1.5 border-slate-700"
                >
                  <ExternalLink className="h-4 w-4" /> Direct Link
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
